import { useMemo, useState } from 'react';
import { requireSatellite } from './config';
import { SatelliteLayout } from './layout';
import { trackSatelliteAction } from './analytics';

const CONFIG = requireSatellite('coparentmoney.com');

type SplitMode = 'equal' | 'income' | 'custom';

function money(value: number): string {
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function CoparentMoneyPage() {
  const [amount, setAmount] = useState('240');
  const [mode, setMode] = useState<SplitMode>('equal');
  const [incomeA, setIncomeA] = useState('70000');
  const [incomeB, setIncomeB] = useState('50000');
  const [customA, setCustomA] = useState('60');
  const [payer, setPayer] = useState<'A' | 'B'>('A');
  const [nameA, setNameA] = useState('Alex');
  const [nameB, setNameB] = useState('Jordan');
  const [description, setDescription] = useState('soccer registration');
  const [copied, setCopied] = useState(false);

  const parsedAmount = Number(amount) || 0;
  const shareA = useMemo(() => {
    if (mode === 'equal') return 0.5;
    if (mode === 'custom') return Math.min(1, Math.max(0, (Number(customA) || 0) / 100));
    const a = Number(incomeA) || 0;
    const b = Number(incomeB) || 0;
    const total = a + b;
    return total <= 0 ? 0.5 : a / total;
  }, [mode, customA, incomeA, incomeB]);

  const dueA = parsedAmount * shareA;
  const dueB = parsedAmount - dueA;
  const payerName = payer === 'A' ? nameA : nameB;
  const otherName = payer === 'A' ? nameB : nameA;
  const otherShare = payer === 'A' ? dueB : dueA;
  const payerShare = payer === 'A' ? dueA : dueB;
  const reimbursement = Math.max(0, parsedAmount - payerShare);

  const letter = `${otherName},

I paid $${parsedAmount.toFixed(2)} for ${description}. Split ${
    mode === 'equal' ? '50/50' : mode === 'custom' ? `${Math.round(shareA * 100)}/${Math.round((1 - shareA) * 100)}` : 'by income'
  }, your share is ${money(otherShare)}.

Please reimburse ${money(reimbursement)} to ${payerName}.

Thank you.`;

  async function copyLetter() {
    try {
      await navigator.clipboard.writeText(letter);
      setCopied(true);
      trackSatelliteAction('Satellite Letter Copied', CONFIG);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <SatelliteLayout config={CONFIG}>
      <form className='grid gap-4 sm:grid-cols-2'>
        <label className='text-sm'>
          <span className='mb-1 block font-medium'>Expense amount</span>
          <input
            type='number'
            min='0'
            step='0.01'
            className='w-full rounded-lg border border-gray-300 px-3 py-2'
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </label>
        <label className='text-sm'>
          <span className='mb-1 block font-medium'>Who paid</span>
          <select
            className='w-full rounded-lg border border-gray-300 px-3 py-2'
            value={payer}
            onChange={(event) => setPayer(event.target.value as 'A' | 'B')}
          >
            <option value='A'>{nameA || 'Parent A'}</option>
            <option value='B'>{nameB || 'Parent B'}</option>
          </select>
        </label>
        <label className='text-sm'>
          <span className='mb-1 block font-medium'>Your name</span>
          <input
            className='w-full rounded-lg border border-gray-300 px-3 py-2'
            value={nameA}
            onChange={(event) => setNameA(event.target.value)}
          />
        </label>
        <label className='text-sm'>
          <span className='mb-1 block font-medium'>Other parent</span>
          <input
            className='w-full rounded-lg border border-gray-300 px-3 py-2'
            value={nameB}
            onChange={(event) => setNameB(event.target.value)}
          />
        </label>
        <label className='text-sm sm:col-span-2'>
          <span className='mb-1 block font-medium'>Split</span>
          <select
            className='w-full rounded-lg border border-gray-300 px-3 py-2'
            value={mode}
            onChange={(event) => setMode(event.target.value as SplitMode)}
          >
            <option value='equal'>50 / 50</option>
            <option value='income'>By income</option>
            <option value='custom'>Custom percent</option>
          </select>
        </label>
        {mode === 'income' ? (
          <>
            <label className='text-sm'>
              <span className='mb-1 block font-medium'>{nameA} income</span>
              <input
                type='number'
                min='0'
                className='w-full rounded-lg border border-gray-300 px-3 py-2'
                value={incomeA}
                onChange={(event) => setIncomeA(event.target.value)}
              />
            </label>
            <label className='text-sm'>
              <span className='mb-1 block font-medium'>{nameB} income</span>
              <input
                type='number'
                min='0'
                className='w-full rounded-lg border border-gray-300 px-3 py-2'
                value={incomeB}
                onChange={(event) => setIncomeB(event.target.value)}
              />
            </label>
          </>
        ) : null}
        {mode === 'custom' ? (
          <label className='text-sm sm:col-span-2'>
            <span className='mb-1 block font-medium'>{nameA} percent</span>
            <input
              type='number'
              min='0'
              max='100'
              className='w-full rounded-lg border border-gray-300 px-3 py-2 sm:w-40'
              value={customA}
              onChange={(event) => setCustomA(event.target.value)}
            />
          </label>
        ) : null}
        <label className='text-sm sm:col-span-2'>
          <span className='mb-1 block font-medium'>What was it for</span>
          <input
            className='w-full rounded-lg border border-gray-300 px-3 py-2'
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
      </form>

      <div className='mt-6 grid gap-3 sm:grid-cols-2'>
        <div className='rounded-2xl border border-gray-200 p-4'>
          <p className='text-xs uppercase text-gray-500'>{nameA} share</p>
          <p className='mt-1 text-2xl font-semibold'>{money(dueA)}</p>
        </div>
        <div className='rounded-2xl border border-gray-200 p-4'>
          <p className='text-xs uppercase text-gray-500'>{nameB} share</p>
          <p className='mt-1 text-2xl font-semibold'>{money(dueB)}</p>
        </div>
      </div>
      <p className='mt-3 text-sm text-gray-600'>
        {otherName} reimburses {payerName} {money(reimbursement)}.
      </p>

      <section className='mt-8'>
        <h2 className='text-lg font-semibold'>Reimbursement request</h2>
        <pre className='mt-3 whitespace-pre-wrap rounded-2xl bg-gray-50 p-4 text-sm text-gray-800'>
          {letter}
        </pre>
        <button
          type='button'
          className='mt-3 rounded-xl bg-primary px-4 py-2 font-semibold text-white hover:bg-primary-hover'
          onClick={() => void copyLetter()}
        >
          {copied ? 'Copied' : 'Copy letter'}
        </button>
      </section>
    </SatelliteLayout>
  );
}
